/**
 * The three things you can do to a sale after it has been finalised, and the record of what
 * has already been done to it.
 *
 * Winnie, 18 and 20 September:
 *
 *  - **Correction history** (§3a): "A logged adjustment that only exists in a table nobody
 *    looks at defeats the point... Ordered most recent first, always visible on the deal —
 *    not hidden behind a separate report someone has to know to go looking for."
 *  - **Amendments** (Checks_20 Sep §7): in place, inside the cooling-off window, same deal and
 *    invoice number.
 *  - **Void** (§9 Path B): the whole cascade, when no replacement buyer is found.
 *
 * The one piece of visual design that is load-bearing rather than decorative: a correction
 * made by bypassing a closed accounting period is drawn differently from an ordinary one.
 * Winnie: "that's exactly the kind of action that should be impossible to miss when scanning
 * the history, not blend in with ordinary corrections."
 */
import { useCallback, useEffect, useState } from 'react'
import { Deal, User } from '../../types'
import { isSuperAdmin } from '../../lib/permissions'
import {
  CORRECTABLE_FIELDS,
  CorrectableField,
  FIELD_LABEL,
  isMoneyField,
} from '../../lib/fieldCorrection'
import { FieldCorrection, correctField, loadCorrections } from '../../services/fieldCorrectionService'
import {
  AMENDMENT_LABEL,
  SaleAmendment,
  amendSale,
  loadAmendments,
  voidSale,
  withinCoolingOff,
} from '../../services/saleAmendmentService'
import { ShippingParty } from '../../services/saleWorkspaceService'
import { toast } from '../Toast'
import './SaleGovernancePanels.css'

const when = (iso?: string | null) =>
  !iso ? '—' : new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })

const shortValue = (field: CorrectableField, v: string | null): string => {
  if (v == null || v === '') return '—'
  if (isMoneyField(field)) {
    const n = Number(v)
    return Number.isFinite(n)
      ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
      : v
  }
  return String(v).slice(0, 10)
}

/* ======================================================================== *
 * Correction history, and the form that adds to it
 * ===================================================================== */

export function CorrectionPanel({ deal, user }: { deal: Deal; user: User }) {
  const [rows, setRows] = useState<FieldCorrection[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [field, setField] = useState<CorrectableField>('sale_price')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    loadCorrections(deal.id)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [deal.id])

  useEffect(load, [load])

  const save = async () => {
    setBusy(true)
    setNote(null)
    try {
      const res = await correctField({
        dealId: deal.id,
        field,
        correctedValue: value.trim(),
        reason: reason.trim(),
        user,
      })
      setNote(res.message)
      if (res.ok) {
        toast.saved()
        setValue('')
        setReason('')
        setOpen(false)
        load()
      } else {
        toast.error(res.message)
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'The correction could not be saved.'
      setNote(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="sd-section">
      <h4 className="sd-section-title">Correction history</h4>
      <p className="sgp-hint">
        The sale price, sold date, purchase price and purchase date are retained figures. Correcting one
        records what it was, what it became and why — and restates the commission and the margin scheme
        from the corrected figure, because a correction means the recorded figure was wrong from the
        start, not that a cost estimate moved.
      </p>

      {loading ? (
        <p className="sgp-hint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="sgp-empty">No corrections have been made to this vehicle.</p>
      ) : (
        <div className="sgp-table-wrap">
          <table className="sgp-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Previous</th>
                <th>Corrected</th>
                <th>Reason</th>
                <th>By</th>
                <th>When</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                /* A bypassed lock is drawn differently, deliberately. */
                <tr key={c.id} className={c.bypassedLock ? 'sgp-bypassed' : ''}>
                  <td>{FIELD_LABEL[c.fieldName]}</td>
                  <td>{shortValue(c.fieldName, c.previousValue)}</td>
                  <td>{shortValue(c.fieldName, c.correctedValue)}</td>
                  <td className="sgp-reason">{c.reason}</td>
                  <td>
                    {c.correctedByName}
                    <div className="sgp-sub">{c.correctedByRole === 'SuperAdmin' ? 'Super Admin' : 'Admin'}</div>
                  </td>
                  <td>{when(c.correctedAt)}</td>
                  <td>
                    {c.bypassedLock && <span className="sgp-badge warn">Bypassed lock</span>}
                    {c.flaggedForBasRevision && <span className="sgp-badge bad">BAS revision</span>}
                    {!c.bypassedLock && !c.flaggedForBasRevision && <span className="sgp-sub">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <div className="sgp-form">
          <div className="sgp-row">
            <label className="sgp-field">
              <span>Field</span>
              <select value={field} onChange={(e) => setField(e.target.value as CorrectableField)}>
                {CORRECTABLE_FIELDS.map((f) => (
                  <option key={f} value={f}>
                    {FIELD_LABEL[f]}
                  </option>
                ))}
              </select>
            </label>
            <label className="sgp-field">
              <span>Corrected value</span>
              <input
                type={isMoneyField(field) ? 'number' : 'date'}
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </label>
          </div>
          <label className="sgp-field wide">
            <span>Reason — required, whatever your role</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What was wrong, and how do we know the new figure is right?"
            />
          </label>
          {!isSuperAdmin(user) && (
            <p className="sgp-hint">
              As an Admin you can correct a figure while its accounting period is still open. A figure
              dated into a closed period needs a Super Admin.
            </p>
          )}
          {note && <div className="sgp-note">{note}</div>}
          <div className="sgp-actions">
            <button className="sd-btn-quiet" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </button>
            <button className="sd-btn-primary" onClick={save} disabled={busy || !value.trim() || !reason.trim()}>
              {busy ? 'Saving…' : 'Save correction'}
            </button>
          </div>
        </div>
      ) : (
        <div className="sgp-actions">
          <button className="sd-btn-secondary" onClick={() => setOpen(true)}>
            Correct a figure
          </button>
        </div>
      )}
    </section>
  )
}

/* ======================================================================== *
 * Amend, and void
 * ===================================================================== */

export function AmendmentPanel({
  deal,
  coolingOffExpiresAt,
  isVoided,
  user,
  onChanged,
}: {
  deal: Deal
  coolingOffExpiresAt: string | null
  isVoided: boolean
  user: User
  onChanged: () => void
}) {
  const [rows, setRows] = useState<SaleAmendment[]>([])
  const [mode, setMode] = useState<'none' | 'amend' | 'reassign' | 'void'>('none')
  const [reason, setReason] = useState('')
  const [price, setPrice] = useState('')
  const [buyerName, setBuyerName] = useState('')
  const [buyerAbn, setBuyerAbn] = useState('')
  const [buyerAddress, setBuyerAddress] = useState('')
  const [shipping, setShipping] = useState<'' | ShippingParty>('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(() => {
    loadAmendments(deal.id)
      .then(setRows)
      .catch(() => setRows([]))
  }, [deal.id])

  useEffect(load, [load])

  const inWindow = withinCoolingOff(coolingOffExpiresAt)

  const runAmend = async (reassigned: boolean) => {
    setBusy(true)
    setNote(null)
    try {
      const buyerTouched = buyerName.trim() || buyerAbn.trim() || buyerAddress.trim()
      const res = await amendSale({
        dealId: deal.id,
        reason: reason.trim(),
        salePrice: price.trim() ? Number(price) : undefined,
        buyer: buyerTouched
          ? {
              buyer_name: buyerName.trim() || undefined,
              buyer_abn: buyerAbn.trim() || undefined,
              buyer_address: buyerAddress.trim() || undefined,
            }
          : undefined,
        shippingParty: shipping || undefined,
        reassigned,
        user,
      })
      setNote(res.message)
      if (res.ok) {
        toast.saved()
        setMode('none')
        setReason('')
        setPrice('')
        setBuyerName('')
        setBuyerAbn('')
        setBuyerAddress('')
        setShipping('')
        load()
        onChanged()
      } else {
        toast.error(res.message)
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'The amendment could not be saved.'
      setNote(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  const runVoid = async () => {
    setBusy(true)
    setNote(null)
    try {
      const res = await voidSale(deal.id, reason.trim(), user)
      setNote(res.message)
      if (res.ok) {
        toast.saved()
        setMode('none')
        setReason('')
        load()
        onChanged()
      } else {
        toast.error(res.message)
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'The sale could not be voided.'
      setNote(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="sd-section">
      <h4 className="sd-section-title">Amendments</h4>
      {isVoided ? (
        <p className="sgp-hint">
          This sale is voided. The vehicle is back in available stock — selling it again is a brand-new
          deal with its own number.
        </p>
      ) : (
        <p className="sgp-hint">
          A buyer or price change inside the seven-day cooling-off window is made in place: the same deal
          number, the same invoice number, and the documents re-issued as a marked revision.{' '}
          {inWindow ? (
            <>The window on this sale is open until {when(coolingOffExpiresAt)}.</>
          ) : (
            <>The window on this sale has closed, so a change now needs a decision rather than an edit.</>
          )}
        </p>
      )}

      {rows.length > 0 && (
        <div className="sgp-table-wrap">
          <table className="sgp-table">
            <thead>
              <tr>
                <th>What</th>
                <th>Reason</th>
                <th>Was</th>
                <th>Became</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    {AMENDMENT_LABEL[a.amendmentType]}
                    {a.withinCoolingOff === false && <div className="sgp-sub">outside the window</div>}
                  </td>
                  <td className="sgp-reason">{a.reason}</td>
                  <td>
                    {a.previousSalePrice != null
                      ? a.previousSalePrice.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
                      : a.previousBuyer?.buyer_name || '—'}
                  </td>
                  <td>
                    {a.newSalePrice != null
                      ? a.newSalePrice.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
                      : a.newBuyer?.buyer_name || '—'}
                  </td>
                  <td>{when(a.amendedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {note && <div className="sgp-note">{note}</div>}

      {!isVoided && mode === 'none' && (
        <div className="sgp-actions">
          <button className="sd-btn-secondary" disabled={!inWindow} onClick={() => setMode('amend')}>
            Amend this sale
          </button>
          {/* §9 Path A. Deliberately NOT gated on the cooling-off window: a shipping delay that
              loses the buyer routinely runs past seven days, and that is the exact case this
              path exists for. It is a reassignment of a live deal, not a late edit of a
              settled one. */}
          <button className="sd-btn-secondary" onClick={() => setMode('reassign')}>
            Reassign to a new buyer
          </button>
          <button className="sd-btn-danger" onClick={() => setMode('void')}>
            Void this sale
          </button>
        </div>
      )}

      {mode === 'amend' && (
        <div className="sgp-form">
          <div className="sgp-row">
            <label className="sgp-field">
              <span>New sale price (leave empty to keep)</span>
              <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
            <label className="sgp-field">
              <span>New buyer name (leave empty to keep)</span>
              <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            </label>
            <label className="sgp-field">
              <span>New buyer ABN</span>
              <input value={buyerAbn} onChange={(e) => setBuyerAbn(e.target.value)} />
            </label>
            <label className="sgp-field wide">
              <span>New buyer address (leave empty to keep)</span>
              <input value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} />
            </label>
          </div>
          <label className="sgp-field wide">
            <span>Reason — required</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <p className="sgp-hint">
            A price change restates the commission and the margin scheme credit against the new terms. A
            buyer-only change does not — those figures do not depend on who the buyer is — but the
            receivable is flagged either way so the chase team knows.
          </p>
          <div className="sgp-actions">
            <button className="sd-btn-quiet" onClick={() => setMode('none')} disabled={busy}>
              Cancel
            </button>
            <button className="sd-btn-primary" onClick={() => runAmend(false)} disabled={busy || !reason.trim()}>
              {busy ? 'Amending…' : 'Amend'}
            </button>
          </div>
        </div>
      )}

      {mode === 'reassign' && (
        <div className="sgp-form">
          <p className="sgp-hint">
            The buyer cancelled — a shipping delay, most often — and a replacement has been found. This
            keeps the same deal number and the same invoice number and updates the buyer in place. The
            vehicle's status is not reset: it stays where it is unless who moves the car changes below.
          </p>
          <div className="sgp-row">
            <label className="sgp-field">
              <span>New buyer name</span>
              <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            </label>
            <label className="sgp-field">
              <span>New buyer ABN</span>
              <input value={buyerAbn} onChange={(e) => setBuyerAbn(e.target.value)} />
            </label>
            <label className="sgp-field wide">
              <span>New buyer address</span>
              <input value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} />
            </label>
            <label className="sgp-field">
              <span>New sale price (leave empty to keep)</span>
              <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
            <label className="sgp-field">
              <span>Who moves the car</span>
              <select value={shipping} onChange={(e) => setShipping(e.target.value as '' | ShippingParty)}>
                <option value="">Unchanged</option>
                <option value="DirectAutoShips">Direct Auto arranges delivery</option>
                <option value="BuyerShips">Buyer collects / arranges transport</option>
              </select>
            </label>
          </div>
          <label className="sgp-field wide">
            <span>Reason — required</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <p className="sgp-hint">
            Changing who moves the car opens the matching freight or pickup workspace and cancels the one
            that is no longer needed. Where the vehicle is already Sold, its status follows: In Transit
            when Direct Auto ships, Delivered when the buyer collects.
          </p>
          <div className="sgp-actions">
            <button className="sd-btn-quiet" onClick={() => setMode('none')} disabled={busy}>
              Cancel
            </button>
            <button className="sd-btn-primary" onClick={() => runAmend(true)} disabled={busy || !reason.trim()}>
              {busy ? 'Reassigning…' : 'Reassign'}
            </button>
          </div>
        </div>
      )}

      {mode === 'void' && (
        <div className="sgp-form">
          <p className="sgp-warn">
            Voiding puts the vehicle back into available stock, takes the commission out of active
            totals, cancels the receivable and marks the documents void. Everything is retained — nothing
            is deleted — but this is not the path for a buyer swap. If a replacement buyer has been
            found, amend the sale instead so the deal keeps its number.
          </p>
          <label className="sgp-field wide">
            <span>Reason — required</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="sgp-actions">
            <button className="sd-btn-quiet" onClick={() => setMode('none')} disabled={busy}>
              Cancel
            </button>
            <button className="sd-btn-danger" onClick={runVoid} disabled={busy || !reason.trim()}>
              {busy ? 'Voiding…' : 'Void this sale'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
